<template>
  <div>
    <v-card>
      <v-card-title>
        Content Packs
        <v-spacer />

        <v-btn v-if="selected.length > 0" class="mr-2 mb-2" color="secondary" @click="showBulkStatusDialog">
          MANAGE SELECTED ({{ selected.length }})
        </v-btn>

        <v-btn color="primary" dark @click="showEditDialog(blankContentPack)">
          CREATE
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="4" offset-md="8">
            <v-text-field
              v-model="dtSearchQuery"
              :append-icon="icons.mdiMagnify"
              label="Search by Display Name"
              single-line
              hide-details
              dense
              outlined
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-data-table
        v-model="selected"
        :headers="dtHeaders"
        :items="filteredContentPacks"
        :expanded.sync="expanded"
        :loading="isTableLoading"
        :items-per-page="30"
        :footer-props="{ 'items-per-page-options': [15, 30, 45] }"
        show-select
        single-expand
        show-expand
        item-key="id"
      >
        <template v-slot:item.fitnessIdsCount="{ item }">
          <span v-html="formatFitnessCounts(item)"></span>
        </template>

        <template v-slot:item.practiceIdsCount="{ item }">
          <span v-html="formatPracticeCounts(item)"></span>
        </template>

        <template v-slot:item.flowIdsCount="{ item }">
          <span v-html="formatFlowCounts(item)"></span>
        </template>

        <template v-slot:item.creator="{ item }">
          <div v-if="item.creator" class="text-body-2 py-2" style="line-height: 1.4;" v-html="item.creator"></div>
          <div v-else class="text-caption grey--text font-italic">
            System
          </div>
        </template>

        <template v-slot:item.academiesDisplay="{ item }">
          <div v-if="item.academies?.length" class="text-body-2 py-2 academy-lines">
            <div v-for="academy in item.academies" :key="academy.id">
              {{ academy.name }}
            </div>
          </div>
          <div v-else class="text-caption grey--text font-italic">
            None
          </div>
        </template>

        <template v-slot:item.active="{ item }">
          <v-chip class="table-status-chip" :color="item.active ? 'success' : 'error'" dark>
            {{ item.active ? 'Yes' : 'No' }}
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-icon class="mr-2" @click.stop="showEditDialog(item)">
            {{ icons.mdiPencilOutline }}
          </v-icon>
          <v-icon @click.stop="showDeleteConfirmationDialog(item)">
            {{ icons.mdiDeleteOutline }}
          </v-icon>
        </template>

        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="pa-0">
            <content-pack-details
              :item="item"
              :is-loading="isLoading"
              @save="onContentPackDetailsSave"
            />
          </td>
        </template>
      </v-data-table>
    </v-card>

    <delete-dialog
      v-model="isDeleteDialogVisible"
      :item="contentPack"
      :is-loading="isLoading"
      title="Delete Content Pack"
      message="Are you sure you want to delete this Content Pack?"
      confirm-label="Continue"
      cancel-label="Cancel"
      @cancel="onDeleteCanceled"
      @delete="onDeleteConfirmed"
    />

    <content-pack-edit-dialog
      v-if="isEditDialogVisible"
      v-model="isEditDialogVisible"
      :item="contentPack"
      :is-loading="isLoading"
      @cancel="onEditCanceled"
      @save="onEditSave"
      @relations-updated="fetchContentPacks"
    />

    <v-dialog v-model="isBulkStatusDialogVisible" max-width="720">
      <v-card>
        <v-card-title>Apply to Selected</v-card-title>
        <v-card-text class="pt-4 pb-2">
          <p>Choose an action for {{ selected.length }} selected item(s).</p>
          <v-radio-group v-model="bulkStatusValue" class="mt-4" hide-details>
            <v-radio
              v-for="option in bulkStatusOptions"
              :key="option.value"
              :value="option.value"
              class="mb-2"
            >
              <template v-slot:label>
                <span :class="option.value === 'delete-selected' ? 'error--text font-weight-medium' : ''">
                  {{ option.text }}
                </span>
              </template>
            </v-radio>
          </v-radio-group>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="isBulkStatusDialogVisible = false">
            Cancel
          </v-btn>
          <v-btn color="primary" :loading="isBulkUpdating" @click="updateBulkStatus">
            Continue
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isBulkDeleteDialogVisible" max-width="500">
      <v-card>
        <v-card-title>Delete Selected Items</v-card-title>
        <v-card-text>
          <p>Are you sure you want to delete <strong>{{ selected.length }}</strong> items?</p>
          <p class="error--text mb-0">
            This action cannot be undone.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="isBulkDeleteDialogVisible = false">
            Cancel
          </v-btn>
          <v-btn color="error" :loading="isBulkDeleting" @click="deleteBulkItems">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isAcademyDialogVisible" max-width="560">
      <v-card>
        <v-card-title>Assign Content Packs to Academy</v-card-title>
        <v-card-text>
          <v-autocomplete
            v-model="selectedAcademyId"
            :items="academyOptions"
            label="Academy"
            outlined
            clearable
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeAcademyDialog">
            Cancel
          </v-btn>
          <v-btn color="primary" :disabled="!selectedAcademyId" @click="showAcademyConfirmationDialog">
            Continue
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isAcademyConfirmationDialogVisible" max-width="720">
      <v-card>
        <v-card-title>Assign Content Packs to Academy</v-card-title>
        <v-card-text>
          <p>{{ academyConfirmationMessage }}</p>
          <v-list dense class="py-0">
            <v-list-item v-for="item in selected" :key="item.id">
              <v-list-item-content>
                <v-list-item-title>{{ item.displayName }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="isAcademyConfirmationDialogVisible = false">
            Cancel
          </v-btn>
          <v-btn color="primary" :loading="isBulkUpdating" @click="applyAcademyContentAction">
            Continue
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isUserDialogVisible" max-width="560">
      <v-card>
        <v-card-title>Assign Content Packs to User</v-card-title>
        <v-card-text>
          <v-autocomplete
            v-model="selectedUserId"
            :items="userOptions"
            label="User"
            outlined
            clearable
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeUserDialog">
            Cancel
          </v-btn>
          <v-btn color="primary" :disabled="!selectedUserId" @click="showUserConfirmationDialog">
            Continue
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isUserConfirmationDialogVisible" max-width="720">
      <v-card>
        <v-card-title>Assign Content Packs to User</v-card-title>
        <v-card-text>
          <p>{{ userConfirmationMessage }}</p>
          <v-list dense class="py-0">
            <v-list-item v-for="item in selected" :key="item.id">
              <v-list-item-content>
                <v-list-item-title>{{ item.displayName }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="isUserConfirmationDialogVisible = false">
            Cancel
          </v-btn>
          <v-btn color="primary" :loading="isBulkUpdating" @click="applyUserContentAction">
            Continue
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  mdiDeleteOutline,
  mdiMagnify,
  mdiPencilOutline,
} from '@mdi/js'
import { computed, onMounted, ref } from 'vue'
import axios from '@/utils/axios'
import useSnackbar, { SnackbarType } from '@/composables/snackbar'
import DeleteDialog from '@/components/DeleteDialog.vue'
import useContentPacks from '@/composables/api/contentPacks'
import useAcademies from '@/composables/api/academies'
import useContentPackAcademies from '@/composables/api/contentPackAcademies'
import useContentPackUsers from '@/composables/api/contentPackUsers'
import useUsers from '@/composables/api/users'

export default {
  components: {
    DeleteDialog,
    ContentPackEditDialog: () => import('@/views/contentPacks/partials/ContentPackEditDialog.vue'),
    ContentPackDetails: () => import('@/views/contentPacks/partials/ContentPackDetails.vue'),
  },
  setup() {
    const {
      dtHeaders,
      dtSearchQuery,
      blankContentPack,
      contentPacks,
      isLoading,
      fetchContentPacks,
      createContentPack,
      updateContentPack,
      deleteContentPack,
    } = useContentPacks()
    const { academies, fetchAcademies } = useAcademies()
    const { users, fetchUsers } = useUsers()
    const { upsertContentPackAcademy } = useContentPackAcademies()
    const { upsertContentPackUser } = useContentPackUsers()

    const selected = ref([])
    const expanded = ref([])
    const contentPack = ref(structuredClone(blankContentPack))
    const isDeleteDialogVisible = ref(false)
    const isEditDialogVisible = ref(false)
    const isBulkStatusDialogVisible = ref(false)
    const bulkStatusValue = ref('active-true')
    const isBulkUpdating = ref(false)
    const isBulkDeleteDialogVisible = ref(false)
    const isBulkDeleting = ref(false)
    const isAcademyDialogVisible = ref(false)
    const isAcademyConfirmationDialogVisible = ref(false)
    const selectedAcademyId = ref(null)
    const isUserDialogVisible = ref(false)
    const isUserConfirmationDialogVisible = ref(false)
    const selectedUserId = ref(null)
    const { showSnackbar } = useSnackbar()
    const bulkStatusOptions = [
      { text: 'Active', value: 'active-true' },
      { text: 'Inactive', value: 'active-false' },
      { text: 'Delete', value: 'delete-selected' },
      { text: 'Assign Content Packs to Academy', value: 'academy-content-add' },
      { text: 'Assign Content Packs to User', value: 'user-content-add' },
    ]

    const contentPacksWithCreators = computed(() => contentPacks.value.map(item => ({
      ...item,
      creator: item.creator || null,
    })))

    const filteredContentPacks = computed(() => {
      const search = dtSearchQuery.value.trim().toLowerCase()
      if (!search) return contentPacksWithCreators.value

      return contentPacksWithCreators.value.filter(item => item.displayName?.toLowerCase().includes(search))
    })
    const academyOptions = computed(() => academies.value.map(item => ({
      text: item.name,
      value: item.id,
    })))
    const selectedAcademy = computed(() => academies.value.find(item => item.id === selectedAcademyId.value) || null)
    const academyConfirmationMessage = computed(() => `Are you sure you want to assign these content packs to academy "${selectedAcademy.value?.name || ''}"?`)
    const userOptions = computed(() => users.value.map(item => ({
      text: `${[item.firstName, item.lastName].filter(Boolean).join(' ')}${item.email ? ` - ${item.email}` : ''}`,
      value: item.id,
    })))
    const selectedUser = computed(() => users.value.find(item => item.id === selectedUserId.value) || null)
    const userConfirmationMessage = computed(() => {
      const userLabel = selectedUser.value
        ? `${[selectedUser.value.firstName, selectedUser.value.lastName].filter(Boolean).join(' ')}${selectedUser.value.email ? ` (${selectedUser.value.email})` : ''}`
        : ''

      return `Are you sure you want to assign these content packs to user "${userLabel}"?`
    })

    const isTableLoading = computed(() => isLoading.value)

    const formatCountBlock = (lineItems, total) => `
      <div style="line-height: 1.35; white-space: normal;">
        ${lineItems.join('<br/>')}<br/>
        <strong class="content-pack-total">Σ = ${total}</strong>
      </div>
    `

    const formatFitnessCounts = item => formatCountBlock([
      `${item.fitnessExercisesIdsCount} Exercises`,
      `${item.fitnessSessionsIdsCount} Routines`,
      `${item.fitnessAssessmentsIdsCount} Assessments`,
    ], item.fitnessIdsCount)

    const formatPracticeCounts = item => formatCountBlock([
      `${item.practiceExercisesIdsCount} Drills`,
      `${item.practiceSessionsIdsCount} Sessions`,
      `${item.practiceAssessmentsIdsCount} Assessments`,
    ], item.practiceIdsCount)

    const formatFlowCounts = item => formatCountBlock([
      `${item.flowExercisesIdsCount} Exercises`,
      `${item.flowSessionsIdsCount} Sessions`,
      `${item.flowAssessmentsIdsCount} Assessments`,
    ], item.flowIdsCount)

    onMounted(fetchContentPacks)

    const showDeleteConfirmationDialog = item => {
      contentPack.value = structuredClone(item)
      isDeleteDialogVisible.value = true
    }

    const onDeleteCanceled = () => {
      contentPack.value = structuredClone(blankContentPack)
      isDeleteDialogVisible.value = false
    }

    const onDeleteConfirmed = async item => {
      const deleted = await deleteContentPack(item)
      if (!deleted) return
      await fetchContentPacks()
      isDeleteDialogVisible.value = false
      contentPack.value = structuredClone(blankContentPack)
    }

    const showEditDialog = itemData => {
      contentPack.value = structuredClone(itemData)

      if (contentPack.value.active === undefined) {
        contentPack.value.active = true
      }

      isEditDialogVisible.value = true
    }

    const onEditCanceled = () => {
      contentPack.value = structuredClone(blankContentPack)
      isEditDialogVisible.value = false
    }

    const onEditSave = async item => {
      try {
        if (!item.id) {
          await createContentPack(item)
        } else {
          await updateContentPack(item)
        }

        await fetchContentPacks()
        isEditDialogVisible.value = false
        contentPack.value = structuredClone(blankContentPack)
      } catch (error) {
        console.error(error)
      }
    }

    const showBulkStatusDialog = () => {
      bulkStatusValue.value = 'active-true'
      isBulkStatusDialogVisible.value = true
    }

    const updateBulkStatus = async () => {
      if (selected.value.length === 0) {
        return
      }

      if (bulkStatusValue.value === 'delete-selected') {
        isBulkStatusDialogVisible.value = false
        isBulkDeleteDialogVisible.value = true

        return
      }

      if (bulkStatusValue.value === 'academy-content-add') {
        await fetchAcademies()
        selectedAcademyId.value = null
        isBulkStatusDialogVisible.value = false
        isAcademyDialogVisible.value = true

        return
      }

      if (bulkStatusValue.value === 'user-content-add') {
        await fetchUsers()
        selectedUserId.value = null
        isBulkStatusDialogVisible.value = false
        isUserDialogVisible.value = true

        return
      }

      isBulkUpdating.value = true
      try {
        await Promise.all(selected.value.map(item => axios.put(`/content-pack/${item.id}`, {
          ...item,
          active: bulkStatusValue.value === 'active-true',
        }, { headers: { 'Content-Type': 'application/vnd.p72.app-1.0+json' } })))
        await fetchContentPacks()
        selected.value = []
        isBulkStatusDialogVisible.value = false
        showSnackbar('Content packs updated successfully', SnackbarType.SUCCESS)
      } catch (error) {
        showSnackbar('Failed to update content packs', SnackbarType.ERROR)
      } finally {
        isBulkUpdating.value = false
      }
    }

    const closeAcademyDialog = () => {
      isAcademyDialogVisible.value = false
      selectedAcademyId.value = null
    }

    const closeUserDialog = () => {
      isUserDialogVisible.value = false
      selectedUserId.value = null
    }

    const showAcademyConfirmationDialog = () => {
      isAcademyDialogVisible.value = false
      isAcademyConfirmationDialogVisible.value = true
    }

    const showUserConfirmationDialog = () => {
      isUserDialogVisible.value = false
      isUserConfirmationDialogVisible.value = true
    }

    const applyAcademyContentAction = async () => {
      if (!selectedAcademyId.value || selected.value.length === 0) {
        return
      }

      isBulkUpdating.value = true
      try {
        await Promise.all(selected.value.map(item => upsertContentPackAcademy(selectedAcademyId.value, item.id, { silent: true })))
        await fetchContentPacks()
        selected.value = []
        isAcademyConfirmationDialogVisible.value = false
        selectedAcademyId.value = null
        showSnackbar('Academy content packs updated successfully', SnackbarType.SUCCESS)
      } finally {
        isBulkUpdating.value = false
      }
    }

    const applyUserContentAction = async () => {
      if (!selectedUserId.value || selected.value.length === 0) {
        return
      }

      isBulkUpdating.value = true
      try {
        await Promise.all(selected.value.map(item => upsertContentPackUser(selectedUserId.value, item.id, { silent: true })))
        await fetchContentPacks()
        selected.value = []
        isUserConfirmationDialogVisible.value = false
        selectedUserId.value = null
        showSnackbar('User content packs updated successfully', SnackbarType.SUCCESS)
      } finally {
        isBulkUpdating.value = false
      }
    }

    const deleteBulkItems = async () => {
      if (selected.value.length === 0) return
      isBulkDeleting.value = true
      try {
        await Promise.all(selected.value.map(item => axios.put(`/content-pack/${item.id}`, {
          ...item,
          deleted: true,
        }, { headers: { 'Content-Type': 'application/vnd.p72.app-1.0+json' } })))
        await fetchContentPacks()
        selected.value = []
        isBulkDeleteDialogVisible.value = false
        showSnackbar('Content packs deleted successfully', SnackbarType.SUCCESS)
      } catch (error) {
        showSnackbar('Failed to delete content packs', SnackbarType.ERROR)
      } finally {
        isBulkDeleting.value = false
      }
    }

    const onContentPackDetailsSave = async item => {
      await updateContentPack(item)
      await fetchContentPacks()
    }

    return {
      dtHeaders,
      dtSearchQuery,
      blankContentPack,
      filteredContentPacks,
      selected,
      expanded,
      contentPack,
      isBulkStatusDialogVisible,
      bulkStatusValue,
      bulkStatusOptions,
      isBulkUpdating,
      isBulkDeleteDialogVisible,
      isBulkDeleting,
      isAcademyDialogVisible,
      isAcademyConfirmationDialogVisible,
      selectedAcademyId,
      academyOptions,
      academyConfirmationMessage,
      isUserDialogVisible,
      isUserConfirmationDialogVisible,
      selectedUserId,
      userOptions,
      userConfirmationMessage,
      isDeleteDialogVisible,
      isEditDialogVisible,
      isTableLoading,
      isLoading,
      formatFitnessCounts,
      formatPracticeCounts,
      formatFlowCounts,
      showDeleteConfirmationDialog,
      onDeleteCanceled,
      onDeleteConfirmed,
      showEditDialog,
      showBulkStatusDialog,
      updateBulkStatus,
      closeAcademyDialog,
      showAcademyConfirmationDialog,
      applyAcademyContentAction,
      closeUserDialog,
      showUserConfirmationDialog,
      applyUserContentAction,
      deleteBulkItems,
      onEditCanceled,
      onEditSave,
      onContentPackDetailsSave,
      icons: {
        mdiDeleteOutline,
        mdiMagnify,
        mdiPencilOutline,
      },
    }
  },
}
</script>
<style scoped>
.v-application.theme--light .content-pack-total {
  color: #1F51FF;
}

.v-application.theme--dark .content-pack-total {
  color: #D6F1FF;
}

.academy-lines {
  white-space: pre-line;
  line-height: 1.4;
}
</style>
